import { Formik, FormikHelpers, FormikProps } from "formik";
import { useCallback, useContext, useEffect, useState } from "react";
import { ObjectSchema, object } from "yup";
import { requiredBoolean } from "../../../data/yupConstants";
import {
  PersonAddressDataNewsletterAndDisclosureSettings,
  PersonNewsletterAndDisclosureSettings,
} from "../../../generated/data-contracts";
import { ErrorUtils, IAxiosError, api } from "../../../utils";
import { AvisForm, AvisLoader } from "../../shared/components";
import { AvisSaveButton } from "../../shared/components/buttons";
import { AvisContext } from "../../shared/context/AvisContext";
import { AuthorNotifications } from "./sections/AuthorNotifications";
import { ContactInformation } from "./sections/ContactInformation";
import { RightsToDisclose } from "./sections/RightsToDisclose";

export function ContactInfoPage() {
  const { res, ctxUser } = useContext(AvisContext);
  const [personSettings, setPersonSettings] =
    useState<PersonAddressDataNewsletterAndDisclosureSettings>();
  const [isPersonSettingsLoading, setIsPersonSettingsLoading] =
    useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<IAxiosError>();

  const isReadOnly = ctxUser?.personContactId
    ? ctxUser.personContactId > 0
    : false;
  useEffect(() => {
    if (!isPersonSettingsLoading) return;

    api
      .rightsHoldersGetPersonSettings()
      .then((res) => setPersonSettings(res.data))
      .catch(setError)
      .finally(() => setIsPersonSettingsLoading(false));
  }, [isPersonSettingsLoading]);

  const validationSchema: ObjectSchema<PersonNewsletterAndDisclosureSettings> =
    object({
      disclosureAddress: requiredBoolean(res),
      disclosureEmail: requiredBoolean(res),
      disclosurePhone: requiredBoolean(res),
      newsletterForAuthors: requiredBoolean(res),
    });

  const handleSubmit = (
    values: PersonNewsletterAndDisclosureSettings,
    formikHelpers: FormikHelpers<PersonNewsletterAndDisclosureSettings>,
  ) => {
    setIsSubmitting(true);

    api
      .rightsHoldersUpdatePersonSettings(values)
      .then(() =>
        ErrorUtils.showSuccessfulMessage(
          res("message_Success"),
          res("message_Success_Content_Common"),
        ),
      )
      .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
      .finally(() => setIsSubmitting(false));
  };

  const renderDisclosureForm = useCallback(
    (formikProps: FormikProps<PersonNewsletterAndDisclosureSettings>) => {
      return (
        <>
          <AuthorNotifications bag={formikProps} isReadOnly={isReadOnly} />
          <RightsToDisclose bag={formikProps} isReadOnly={isReadOnly} />
          {!isReadOnly && (
            <AvisSaveButton
              onClick={formikProps.submitForm}
              disabled={isSubmitting}
            />
          )}
        </>
      );
    },
    [isSubmitting, isReadOnly],
  );

  const renderForm = () => {
    if (isPersonSettingsLoading) return <AvisLoader inline />;

    return (
      <>
        <ContactInformation personData={personSettings} />
        <Formik<PersonNewsletterAndDisclosureSettings>
          initialValues={{
            disclosureAddress: personSettings?.disclosureAddress,
            disclosureEmail: personSettings?.disclosureEmail,
            disclosurePhone: personSettings?.disclosurePhone,
            newsletterForAuthors: personSettings?.newsletterForAuthors,
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {renderDisclosureForm}
        </Formik>
      </>
    );
  };

  return (
    <AvisForm
      error={error}
      className="contact-information-form"
      title={res("authors_ContactInfoPage_Title")}
    >
      {renderForm()}
    </AvisForm>
  );
}
